import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, FormFeedback, Button } from 'reactstrap';
import { handleAddModifyOperation, fetchFormData } from '../../../../Api/ApiMethods';
import { toast } from 'react-toastify';
import FilesComponent from '../../../Pages/CallForms/Component/FilesComponent';
import PropTypes from 'prop-types';

// Define a constant empty array outside the component
const EMPTY_ARRAY = [];

// Helper function to format date to YYYY-MM-DD for date input
const toLocalISOStringDateOnly = (date) => {
    if (!date) return '';
    try {
        const dateObj = new Date(date);
        if (isNaN(dateObj.getTime())) return '';
        // Format to YYYY-MM-DD
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    } catch (error) {
        console.error("Error formatting date:", error);
        return '';
    }
};

const PowerOfAttorneyDataModal = ({ isOpen, toggle, mode, selectedPowerOfAttorney, refreshData, customerId }) => {    // Use useRef for initial state to avoid re-creation
    const initialFormStateRef = useRef({
        poaNumber: '',
        poaDate: '', // Date only
        isPoaTimeLimited: false,
        poaEndDate: '', // Date only
        notaryOfPoa: '',
        isLicensePlatesLimited: false,
        licensePlateLimit: [], // Array of IDs
        isAccidentDateLimited: false,
        licensePlateLimitAccidentDate: '', // Date only
        poaOriginalArrived: false,
        customer: customerId // Ensure customerId is in an array
    });

    const [formData, setFormData] = useState(initialFormStateRef.current);
    const [errors, setErrors] = useState({});
    const [selectedPlates, setSelectedPlates] = useState([]); // Array of plate objects for display
    const [availablePlates, setAvailablePlates] = useState([]); // Array of available plate objects
    const [selectedPlateId, setSelectedPlateId] = useState(''); // State for the selected plate ID from dropdown
    const [currentPoaId, setCurrentPoaId] = useState(null); // State for current POA ID (for file uploads)

    useEffect(() => {
        const loadData = async () => {
            if (isOpen) { // Only run when modal is open
                if (mode === 'modify' && selectedPowerOfAttorney) {
                    try {
                        // Fetch details of limited plates if they exist
                        const plates = [];
                        const plateIds = Array.isArray(selectedPowerOfAttorney.licensePlateLimit)
                            ? selectedPowerOfAttorney.licensePlateLimit
                            : selectedPowerOfAttorney.licensePlateLimit ? [selectedPowerOfAttorney.licensePlateLimit] : [];

                        if (plateIds.length > 0) {
                            // Use Promise.all for potentially faster loading if API supports concurrent requests
                            const platePromises = plateIds.map(plateId =>
                                fetchFormData({
                                    // Use correct environment variable for license plate API
                                    apiEndpoint: `${process.env.REACT_APP_LACIVERT_API_ENDPOINT}/licenseplate/${plateId}`,
                                    page: 1,
                                    pageSize: 1
                                }).catch(err => {
                                    console.error(`Error fetching plate ${plateId}:`, err);
                                    return null; // Return null on error for specific plate
                                })
                            );
                            const plateResponses = await Promise.all(platePromises);
                            plateResponses.forEach(response => {
                                // Handle different API response structures and filter out nulls
                                const plateData = response?.data || response;
                                if (plateData) {
                                    plates.push(plateData);
                                }
                            });

                        }
                        setSelectedPlates(plates); // Set the fetched plate objects

                        // Format dates safely using helper
                        const formattedPoaDate = toLocalISOStringDateOnly(selectedPowerOfAttorney.poaDate);
                        const formattedPoaEndDate = toLocalISOStringDateOnly(selectedPowerOfAttorney.poaEndDate);
                        const formattedAccidentDate = toLocalISOStringDateOnly(selectedPowerOfAttorney.licensePlateLimitAccidentDate);                        // Set form data from selected item, ensuring all fields from initial state exist
                        setFormData({
                            ...initialFormStateRef.current, // Start with initial state structure
                            ...selectedPowerOfAttorney, // Override with selected data
                            customer: selectedPowerOfAttorney.customer || customerId,
                            poaDate: formattedPoaDate,
                            poaEndDate: formattedPoaEndDate,
                            licensePlateLimitAccidentDate: formattedAccidentDate,
                            // Ensure licensePlateLimit is always an array of IDs (strings)
                            licensePlateLimit: plateIds.map(id => String(id)),
                        });
                        setCurrentPoaId(selectedPowerOfAttorney.id); // Set current POA ID for file uploads
                        setErrors({}); // Clear previous errors

                    } catch (error) {
                        console.error('Vekaletname veya plaka yükleme hatası:', error);
                        toast.error('Vekaletname bilgileri yüklenirken bir hata oluştu.');                        // Reset to initial state on error
                        setFormData({ ...initialFormStateRef.current, customer: customerId });
                        setSelectedPlates([]);
                        setCurrentPoaId(null); // Reset POA ID on error
                        setErrors({});
                    }
                } else {
                    // Add mode: reset form to initial state but keep customerId
                    resetForm(); // Calls resetForm which handles initial state and customerId
                }
                // Reset states specific to the modal instance when opening
                 setSelectedPlateId('');
                 // availablePlates will be loaded by the other useEffect if needed
            }
        };

        loadData();
    }, [mode, selectedPowerOfAttorney, customerId, isOpen]); // isOpen dependency added

    // Effect to load customer's available plates when plate limitation is enabled
    useEffect(() => {
        const loadCustomerPlates = async () => {
            // Only load if limitation is active AND modal is open AND customerId exists
            if (formData.isLicensePlatesLimited && isOpen && customerId) {
                try {
                    // Use correct environment variable for license plate API with customer filter
                    const apiEndpoint = `${process.env.REACT_APP_LACIVERT_API_ENDPOINT}/licenseplate?customer=${customerId}`;
                    const response = await fetchFormData({
                        apiEndpoint: apiEndpoint,
                        page: 1,
                        pageSize: 1000 // Fetch all plates for the customer
                    });

                    // Handle different API response formats
                    const platesData = response?.data?.items || response?.items || (Array.isArray(response) ? response : []);
                    setAvailablePlates(platesData);

                } catch (error) {
                    console.error('Müşteri plakaları yükleme hatası:', error);
                    toast.error('Müşterinin plakaları yüklenirken bir hata oluştu.');
                    setAvailablePlates([]); // Reset on error
                }
            } else {
                // If limitation is off or modal closed, clear the available plates
                setAvailablePlates([]);
            }
        };

        loadCustomerPlates();
    }, [formData.isLicensePlatesLimited, customerId, isOpen]); // isOpen dependency added

    // Form validation logic
    const validateForm = () => {
        const newErrors = {};

        // POA Date (Required)
        if (!formData.poaDate) {
            newErrors.poaDate = 'Vekalet tarihi zorunludur.';
        } else {
             // Optional: Validate date format/range
             try { new Date(formData.poaDate); } catch (e) { newErrors.poaDate = 'Geçersiz tarih formatı.'; }
        }

        // POA End Date (Required if time limited)
        if (formData.isPoaTimeLimited && !formData.poaEndDate) {
            newErrors.poaEndDate = 'Süreli vekalet için bitiş tarihi zorunludur.';
        } else if (formData.isPoaTimeLimited && formData.poaEndDate && formData.poaDate && new Date(formData.poaEndDate) < new Date(formData.poaDate)) {
             newErrors.poaEndDate = 'Bitiş tarihi, vekalet tarihinden önce olamaz.';
        }

        // Notary (Optional, but validate length if present)
        if (formData.notaryOfPoa && formData.notaryOfPoa.trim().length < 3) {
             newErrors.notaryOfPoa = 'Noter adı en az 3 karakter olmalıdır.';
        }

        // License Plate Limit (Required if plate limitation is active)
        if (formData.isLicensePlatesLimited && (!formData.licensePlateLimit || formData.licensePlateLimit.length === 0)) {
            newErrors.licensePlateLimit = 'Plaka sınırlaması seçildiğinde en az bir plaka eklenmelidir.';
        }

        // Accident Date Limit (Required if accident date limitation is active)
        if (formData.isLicensePlatesLimited && formData.isAccidentDateLimited && !formData.licensePlateLimitAccidentDate) {
            newErrors.licensePlateLimitAccidentDate = 'Kaza tarihi sınırlaması seçildiğinde tarih zorunludur.';
        } else if (formData.isLicensePlatesLimited && formData.isAccidentDateLimited && formData.licensePlateLimitAccidentDate) {
             // Optional: Validate date format/range
             try { new Date(formData.licensePlateLimitAccidentDate); } catch (e) { newErrors.licensePlateLimitAccidentDate = 'Geçersiz tarih formatı.'; }
        }


        setErrors(newErrors);
        return Object.keys(newErrors).length === 0; // True if no errors
    };

    // Universal input change handler
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;

        let processedValue = type === 'checkbox' ? checked : value;

        // Fields to convert to uppercase (Turkish locale)
        const fieldsToUppercase = ['notaryOfPoa'];

        // Apply uppercase transformation if applicable
        if (fieldsToUppercase.includes(name) && typeof value === 'string') {
            processedValue = value.toLocaleUpperCase('tr-TR');
        }
        // Handle specific fields if needed (e.g., numeric formatting)
        // else if (name === 'poaNumber') { ... } // Example if poaNumber was editable

        // Update state
        setFormData(prev => ({
            ...prev,
            [name]: processedValue
        }));


        // Clear validation error for the field being changed
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: undefined }));
        }
    };    // Reset form state and errors, keeping essential IDs like customerId
    const resetForm = () => {
        setFormData({ ...initialFormStateRef.current, customer: customerId });
        setErrors({});
        setSelectedPlates([]);
        setAvailablePlates([]);
        setSelectedPlateId('');
        setCurrentPoaId(null); // Reset current POA ID
    };

    // Modal kapatıldığında state'leri sıfırlayan ve tabloyu yenileyen fonksiyon
    const handleModalClose = () => {
        resetForm(); // Reset state completely
        toggle(); // Close the modal using the passed toggle function
    };

    // Callback for FilesComponent updates (FilesComponent kendi içinde dosya yönetimini yapıyor)
    const handleFileSelectionChange = useCallback((filesData) => {
        // FilesComponent kendi içinde dosya yönetimini yaptığı için burada bir şey yapmıyoruz
        // Sadece prop gereksinimi için boş callback
    }, []); // No dependencies needed

    // Handle form submission (Add or Modify)
    const handleSubmit = async () => {
        if (!validateForm()) { // Validate first
            toast.error('Lütfen formdaki hataları düzeltin.');
            // Focus on the first invalid field (optional enhancement)
            const firstErrorKey = Object.keys(errors).find(key => errors[key]);
            if (firstErrorKey) {
                 // Need to handle focusing on the select input or the plate list div if error is there
                const errorElement = document.getElementById(firstErrorKey);
                if (errorElement) errorElement.focus();
                else if (firstErrorKey === 'licensePlateLimit') {
                     // Focus on the select input if the error is about the limit
                     document.getElementById('licensePlateSelect')?.focus();
                }
            }
            return;
        }

        try {
            // Helper to format date string to ISO or return undefined
            const formatIsoDate = (dateString) => {
                 if (!dateString || !/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return undefined;
                 try {
                     // Send only Date part in ISO format YYYY-MM-DD
                     return new Date(dateString).toISOString().split('T')[0];
                 } catch (e) {
                     return undefined;
                 }
             };            // Construct the base payload from formData (already contains uppercase values)
            const payload = {
                customer: customerId, // Ensure customer is always string array - backend expects array
                poaDate: formatIsoDate(formData.poaDate),
                isPoaTimeLimited: Boolean(formData.isPoaTimeLimited),
                poaEndDate: formData.isPoaTimeLimited ? formatIsoDate(formData.poaEndDate) : undefined,
                notaryOfPoa: formData.notaryOfPoa || "", // Already uppercase
                isLicensePlatesLimited: Boolean(formData.isLicensePlatesLimited),
                // Send only the array of plate IDs (strings)
                licensePlateLimit: formData.isLicensePlatesLimited ? (formData.licensePlateLimit || []) : [],
                isAccidentDateLimited: formData.isLicensePlatesLimited ? Boolean(formData.isAccidentDateLimited) : false,
                licensePlateLimitAccidentDate: formData.isLicensePlatesLimited && formData.isAccidentDateLimited
                    ? formatIsoDate(formData.licensePlateLimitAccidentDate)
                    : undefined,
                poaOriginalArrived: Boolean(formData.poaOriginalArrived),
                ...(mode === 'modify' && selectedPowerOfAttorney?.id && { id: selectedPowerOfAttorney.id })
            };

            // Add poaNumber only if it has a value (assuming it might be optional or generated)
            if (formData.poaNumber) {
                 payload.poaNumber = formData.poaNumber;
            }

            // API endpoint - Use correct environment variable
            const apiEndpoint = `${process.env.REACT_APP_LACIVERT_API_ENDPOINT}/powerofattorney`;
            if (!apiEndpoint) {
                console.error("HATA: Vekaletname API endpoint tanımlanmamış (REACT_APP_LACIVERT_API_ENDPOINT)!");
                toast.error("API endpoint yapılandırması eksik.");
                return;
            }

            console.log("Sending JSON payload:", payload); // Log JSON payload
            
            // FilesComponent kullanıldığı için dosya gönderme mantığı kaldırıldı
            // Sadece JSON payload gönder
            const response = await handleAddModifyOperation({
                apiEndpoint: apiEndpoint,
                selectedItem: mode === 'modify' ? { id: selectedPowerOfAttorney.id } : null,
                mode,
                data: payload, // Send JSON
                refreshDataCallback: refreshData, // Refresh list after success
                componentName: 'Vekaletname',
                isFormData: false // Indicate JSON
            });

            // Check response from handleAddModifyOperation if needed (it should handle toasts)
            if (response?.isSuccess && response?.data?.id) {
                // Set current POA ID for file uploads after successful save
                setCurrentPoaId(response.data.id);
            }

            handleModalClose(); // Close modal on success

        } catch (error) {
            // Error handling is likely done within handleAddModifyOperation
            console.error('Vekalet kaydetme/güncelleme hatası:', error);
            // toast.error('İşlem sırasında beklenmedik bir hata oluştu.'); // Optional fallback toast
        }
    };

    // Plaka Ekleme
    const handleAddPlate = () => {
        if (!selectedPlateId) {
            toast.warning('Lütfen eklenecek plakayı seçin.');
            return;
        }

        const plateToAdd = availablePlates.find(p => p.id === selectedPlateId);
        // Check if it exists and is not already selected
        if (plateToAdd && !selectedPlates.some(p => p.id === plateToAdd.id)) {
            // Update the list of selected plate objects for display
            setSelectedPlates(prev => [...prev, plateToAdd]);
            // Update formData with the new plate ID (ensure it's a string)
            setFormData(prev => ({
                ...prev,
                licensePlateLimit: [...(prev.licensePlateLimit || []), String(plateToAdd.id)]
            }));
            setSelectedPlateId(''); // Reset dropdown selection
            // Clear potential error for licensePlateLimit
            if (errors.licensePlateLimit) {
                setErrors(prev => ({ ...prev, licensePlateLimit: undefined }));
            }
        } else if (selectedPlates.some(p => p.id === selectedPlateId)) {
            toast.info('Bu plaka zaten eklenmiş.');
        } else {
            toast.error('Seçilen plaka bulunamadı.'); // Should not happen normally
        }
    };

    // Plaka Kaldırma
    const handlePlateRemove = (plateIdToRemove) => {
         const stringPlateId = String(plateIdToRemove);
        // Remove from the display list
        setSelectedPlates(prev => prev.filter(p => String(p.id) !== stringPlateId));
        // Remove ID from formData (ensure comparison with string IDs)
        setFormData(prev => ({
            ...prev,
            licensePlateLimit: (prev.licensePlateLimit || []).filter(id => String(id) !== stringPlateId)
        }));
    };

    return (
        // Use handleModalClose for the main toggle to ensure state reset
        <Modal isOpen={isOpen} toggle={handleModalClose} size="lg" backdrop="static"> {/* backdrop="static" prevents closing on outside click */}
            <ModalHeader toggle={handleModalClose}>
                {mode === 'add' ? 'Vekaletname Ekle' : 'Vekaletname Düzenle'}
            </ModalHeader>
            <ModalBody>
                <Form>
                    {/* Vekalet Numarası (Genellikle otomatik atanır, disabled?) */}
                    <FormGroup>
                        <Label for="poaNumber">Vekalet Numarası</Label>
                        <Input
                            id="poaNumber"
                            name="poaNumber"
                            value={formData.poaNumber || ''}
                            onChange={handleInputChange} // Use standard handler
                            placeholder="Vekalet Numarası (Opsiyonel)"
                            disabled={true}
                            invalid={!!errors.poaNumber}
                        />
                        <FormFeedback>{errors.poaNumber}</FormFeedback>
                    </FormGroup>

                    {/* Vekalet Tarihi */}
                    <FormGroup>
                        <Label for="poaDate">Vekalet Tarihi <span className="text-danger">*</span></Label>
                        <Input
                            type="date" // Use date type
                            id="poaDate"
                            name="poaDate"
                            value={formData.poaDate || ''} // Value needs YYYY-MM-DD
                            onChange={handleInputChange}
                            invalid={!!errors.poaDate}
                        />
                        <FormFeedback>{errors.poaDate}</FormFeedback>
                    </FormGroup>

                    {/* Vekalet Süresi */}
                    <FormGroup className="d-flex align-items-center mb-3">
                         {/* Use flexbox for better alignment */}
                        <Label htmlFor="isPoaTimeLimited" className="me-3 mb-0" style={{ flexShrink: 0, minWidth: '180px' }}>Vekalet Süreli Mi?</Label>
                        <div className="d-flex flex-grow-1">
                            <div className="form-check form-check-inline radio radio-success me-3">
                                <Input
                                    className="form-check-input" // Bootstrap 5 class
                                    id="isPoaTimeLimitedTrue"
                                    type="radio"
                                    name="isPoaTimeLimited"
                                    checked={formData.isPoaTimeLimited === true}
                                    onChange={() => handleInputChange({ target: { name: 'isPoaTimeLimited', value: true, type: 'radio' } })}
                                />
                                <Label className="form-check-label" htmlFor="isPoaTimeLimitedTrue">Evet</Label>
                            </div>
                            <div className="form-check form-check-inline radio radio-danger">
                                <Input
                                    className="form-check-input"
                                    id="isPoaTimeLimitedFalse"
                                    type="radio"
                                    name="isPoaTimeLimited"
                                    checked={formData.isPoaTimeLimited === false}
                                    onChange={() => {
                                         handleInputChange({ target: { name: 'isPoaTimeLimited', value: false, type: 'radio' } });
                                         // Also clear end date when switching to No
                                         setFormData(prev => ({ ...prev, poaEndDate: '' }));
                                         if(errors.poaEndDate) setErrors(prev => ({...prev, poaEndDate: ''})); // Clear error too
                                    }}
                                />
                                <Label className="form-check-label" htmlFor="isPoaTimeLimitedFalse">Hayır</Label>
                            </div>
                        </div>
                    </FormGroup>

                    {/* Vekalet Bitiş Tarihi (Conditional) */}
                    {formData.isPoaTimeLimited && (
                        <FormGroup>
                            <Label for="poaEndDate">Vekalet Bitiş Tarihi <span className="text-danger">*</span></Label>
                            <Input
                                type="date" // Use date type
                                id="poaEndDate"
                                name="poaEndDate"
                                value={formData.poaEndDate || ''} // Value needs YYYY-MM-DD
                                onChange={handleInputChange}
                                invalid={!!errors.poaEndDate}
                                min={formData.poaDate} // Set min date based on start date
                            />
                            <FormFeedback>{errors.poaEndDate}</FormFeedback>
                        </FormGroup>
                    )}

                    {/* Noter */}
                    <FormGroup>
                        <Label for="notaryOfPoa">Noter</Label>
                        <Input
                            id="notaryOfPoa"
                            name="notaryOfPoa"
                            value={formData.notaryOfPoa || ''}
                            onChange={handleInputChange} // Use standard handler
                            placeholder="Noter Adı (Örn: Ankara 15. Noter) (Opsiyonel)"
                            invalid={!!errors.notaryOfPoa}
                        />
                        <FormFeedback>{errors.notaryOfPoa}</FormFeedback>
                    </FormGroup>

                     {/* Plaka Sınırlaması */}
                    <FormGroup className="d-flex align-items-center mb-3">
                        <Label htmlFor="isLicensePlatesLimited" className="me-3 mb-0" style={{ flexShrink: 0, minWidth: '180px' }}>Plaka Sınırlaması Var Mı?</Label>
                        <div className="d-flex flex-grow-1">
                            <div className="form-check form-check-inline radio radio-success me-3">
                                <Input
                                    className="form-check-input"
                                    id="isLicensePlatesLimitedTrue"
                                    type="radio"
                                    name="isLicensePlatesLimited"
                                    checked={formData.isLicensePlatesLimited === true}
                                     onChange={() => handleInputChange({ target: { name: 'isLicensePlatesLimited', value: true, type: 'radio' } })}
                                />
                                <Label className="form-check-label" htmlFor="isLicensePlatesLimitedTrue">Evet</Label>
                            </div>
                            <div className="form-check form-check-inline radio radio-danger">
                                <Input
                                    className="form-check-input"
                                    id="isLicensePlatesLimitedFalse"
                                    type="radio"
                                    name="isLicensePlatesLimited"
                                    checked={formData.isLicensePlatesLimited === false}
                                    onChange={() => {
                                         handleInputChange({ target: { name: 'isLicensePlatesLimited', value: false, type: 'radio' } });
                                         // Clear related fields when switching to No
                                         setFormData(prev => ({
                                             ...prev,
                                             licensePlateLimit: [],
                                             isAccidentDateLimited: false,
                                             licensePlateLimitAccidentDate: ''
                                         }));
                                         setSelectedPlates([]); // Clear display list
                                         if(errors.licensePlateLimit) setErrors(prev => ({...prev, licensePlateLimit: ''})); // Clear error
                                    }}
                                />
                                <Label className="form-check-label" htmlFor="isLicensePlatesLimitedFalse">Hayır</Label>
                            </div>
                        </div>
                    </FormGroup>

                     {/* Plaka Seçimi ve Kaza Tarihi (Conditional) */}
                    {formData.isLicensePlatesLimited && (
                        <>
                            {/* Plaka Seçim Dropdown ve Ekle Butonu */}
                            <FormGroup>
                                 {/* Add ID for focusing */}
                                <Label for="licensePlateSelect">Sınırlı Plakalar {selectedPlates.length > 0 ? `(${selectedPlates.length} adet)`: ''} <span className="text-danger">*</span></Label>
                                <div className="input-group">
                                    <Input
                                        type="select"
                                        id="licensePlateSelect"
                                        value={selectedPlateId}
                                        onChange={(e) => setSelectedPlateId(e.target.value)}
                                        className="form-select" // Bootstrap 5 class
                                        disabled={availablePlates.length === 0} // Disable if no plates loaded
                                    >
                                        <option value="">{availablePlates.length === 0 ? 'Müşteri plakaları yükleniyor/yok...' : 'Plaka Seçin...'}</option>
                                        {/* Filter out already selected plates */}
                                        {availablePlates
                                            .filter(plate => !selectedPlates.some(p => String(p.id) === String(plate.id))) // Compare as strings
                                            .map(plate => (
                                                <option key={plate.id} value={String(plate.id)}> {/* Ensure value is string */}
                                                    {plate.licensePlateNumber} {plate.carBrand ? `(${plate.carBrand})` : ''}
                                                </option>
                                            ))
                                        }
                                    </Input>
                                    <Button
                                        color="primary"
                                        onClick={handleAddPlate}
                                        disabled={!selectedPlateId} // Disable if no plate selected
                                    >
                                        Ekle
                                    </Button>
                                </div>
                                {/* Display error specifically for plate limit */}
                                 {/* Add ID for error feedback association */}
                                <FormFeedback style={{ display: errors.licensePlateLimit ? 'block' : 'none' }} id="licensePlateLimitFeedback">
                                    {errors.licensePlateLimit}
                                </FormFeedback>
                            </FormGroup>


                            {/* Seçili Plakalar Listesi */}
                            {selectedPlates.length > 0 && (
                                <div className="selected-plates mb-3 border p-2 rounded bg-light">
                                    {selectedPlates.map(plate => (
                                        <div
                                            key={plate.id}
                                            className="d-flex justify-content-between align-items-center py-1 border-bottom"
                                        >
                                            <span className="text-dark">
                                                {plate.licensePlateNumber} {plate.carBrand ? `- ${plate.carBrand}` : ''} {plate.carModel || ''}
                                            </span>
                                            <Button
                                                size="sm"
                                                color="danger"
                                                outline
                                                onClick={() => handlePlateRemove(plate.id)}
                                                aria-label={`${plate.licensePlateNumber} plakasını kaldır`}
                                            >
                                                &times; {/* Use times symbol for remove */}
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}


                            {/* Kaza Tarihi Sınırlaması */}
                            <FormGroup className="d-flex align-items-center mb-3">
                                <Label htmlFor="isAccidentDateLimited" className="me-3 mb-0" style={{ flexShrink: 0, minWidth: '180px' }}>Kaza Tarihi Sınırlaması?</Label>
                                <div className="d-flex flex-grow-1">
                                    <div className="form-check form-check-inline radio radio-success me-3">
                                        <Input
                                            className="form-check-input"
                                            id="isAccidentDateLimitedTrue"
                                            type="radio"
                                            name="isAccidentDateLimited"
                                            checked={formData.isAccidentDateLimited === true}
                                             onChange={() => handleInputChange({ target: { name: 'isAccidentDateLimited', value: true, type: 'radio' } })}
                                        />
                                        <Label className="form-check-label" htmlFor="isAccidentDateLimitedTrue">Evet</Label>
                                    </div>
                                    <div className="form-check form-check-inline radio radio-danger">
                                        <Input
                                            className="form-check-input"
                                            id="isAccidentDateLimitedFalse"
                                            type="radio"
                                            name="isAccidentDateLimited"
                                            checked={formData.isAccidentDateLimited === false}
                                             onChange={() => {
                                                 handleInputChange({ target: { name: 'isAccidentDateLimited', value: false, type: 'radio' } });
                                                 // Clear date when switching to No
                                                 setFormData(prev => ({ ...prev, licensePlateLimitAccidentDate: '' }));
                                                  if(errors.licensePlateLimitAccidentDate) setErrors(prev => ({...prev, licensePlateLimitAccidentDate: ''}));
                                             }}
                                        />
                                        <Label className="form-check-label" htmlFor="isAccidentDateLimitedFalse">Hayır</Label>
                                    </div>
                                </div>
                            </FormGroup>

                            {/* Plaka Kaza Tarihi (Conditional) */}
                            {formData.isAccidentDateLimited && (
                                <FormGroup>
                                    <Label for="licensePlateLimitAccidentDate">Plaka Kaza Tarihi <span className="text-danger">*</span></Label>
                                    <Input
                                        type="date" // Use date type
                                        id="licensePlateLimitAccidentDate"
                                        name="licensePlateLimitAccidentDate"
                                        value={formData.licensePlateLimitAccidentDate || ''} // Value needs YYYY-MM-DD
                                        onChange={handleInputChange}
                                        invalid={!!errors.licensePlateLimitAccidentDate}
                                    />
                                    <FormFeedback>{errors.licensePlateLimitAccidentDate}</FormFeedback>
                                </FormGroup>
                            )}
                        </>
                    )}

                    {/* Aslı Geldi Mi? */}
                    <FormGroup className="d-flex align-items-center mb-3">
                        <Label htmlFor="poaOriginalArrived" className="me-3 mb-0" style={{ flexShrink: 0, minWidth: '180px' }}>Aslı Geldi Mi?</Label>
                        <div className="d-flex flex-grow-1">
                            <div className="form-check form-check-inline radio radio-success me-3">
                                <Input
                                    className="form-check-input"
                                    id="poaOriginalArrivedTrue"
                                    type="radio"
                                    name="poaOriginalArrived"
                                    checked={formData.poaOriginalArrived === true}
                                    onChange={() => handleInputChange({ target: { name: 'poaOriginalArrived', value: true, type: 'radio' } })}
                                />
                                <Label className="form-check-label" htmlFor="poaOriginalArrivedTrue">Evet</Label>
                            </div>
                            <div className="form-check form-check-inline radio radio-danger">
                                <Input
                                    className="form-check-input"
                                    id="poaOriginalArrivedFalse"
                                    type="radio"
                                    name="poaOriginalArrived"
                                    checked={formData.poaOriginalArrived === false}
                                    onChange={() => handleInputChange({ target: { name: 'poaOriginalArrived', value: false, type: 'radio' } })}
                                />
                                <Label className="form-check-label" htmlFor="poaOriginalArrivedFalse">Hayır</Label>
                            </div>
                        </div>
                    </FormGroup>                    {/* Dosya Yükleme */}
                    <FormGroup className="mt-4">
                        <FilesComponent
                            onFilesChange={handleFileSelectionChange}
                            callFormReference={false} // PowerOfAttorney için çağrı formu değil
                            referenceId={currentPoaId} // Current POA ID (null for new records)
                            typeFilter={['VEKALETNAME']} // Sadece VEKALETNAME türü dosyalar
                            title="Vekaletname Belgeleri"
                            multiple={false} // Tek dosya yükleme
                            canEdit={true}
                        />
                    </FormGroup>
                </Form>
            </ModalBody>
            <ModalFooter>
                {/* Use standard Buttons */}
                <Button color="primary" onClick={handleSubmit}>
                    {mode === 'add' ? 'Ekle' : 'Güncelle'}
                </Button>
                <Button color="secondary" onClick={handleModalClose} className="ms-2">
                    İptal
                </Button>
            </ModalFooter>
        </Modal>
    );
};

// Add PropTypes
PowerOfAttorneyDataModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    toggle: PropTypes.func.isRequired,
    mode: PropTypes.oneOf(['add', 'modify']).isRequired,
    selectedPowerOfAttorney: PropTypes.object,
    refreshData: PropTypes.func.isRequired,
    customerId: PropTypes.string.isRequired, // Assuming customerId is always required
};

export default PowerOfAttorneyDataModal;
